import { useSyncExternalStore } from "react";
import supabase from "../../utils/supabase";

const BACKEND_API_URL = import.meta.env.VITE_BACKEND_API_URL;
const EVENT_QUEUE_VERSION = 2;
export const analyticsClientInstanceId = crypto.randomUUID();
let clientSequence = 0;
const MAX_QUEUED_EVENTS = 500;
const EVENT_BATCH_SIZE = 25;
const FAILURE_THRESHOLD = 2;

export type AnalyticsDeliveryStatus =
  | "unknown"
  | "healthy"
  | "degraded"
  | "offline"
  | "unauthenticated";

export type AnalyticsDeliverySnapshot = {
  status: AnalyticsDeliveryStatus;
  consecutiveFailures: number;
  pendingEventCount: number;
  lastFailureAt: number | null;
  lastSuccessAt: number | null;
  droppedEventCount: number;
  rejectedEventCount: number;
  storageFailureCount: number;
};

export type QueuedAnalyticsEvent = {
  client_event_id: string;
  event_type: string;
  event_data?: Record<string, unknown>;
  lesson_id?: number;
  unit_id?: number;
  course_id?: number;
  content_revision?: number;
  video_revision?: number;
  quiz_revision?: number;
  coding_revision?: number;
  timestamp: string;
  schema_version?: number;
  client_instance_id?: string;
  client_sequence?: number;
  app_build?: string;
};

type EventQueueEntry = {
  studentId: string;
  appSessionId: number | null;
  lessonVisitId: number | null;
  event: QueuedAnalyticsEvent;
  request?: { endpoint: string; payload: Record<string, unknown> };
};

type EventBatchResponse = {
  events_recorded?: number;
  events_deduplicated?: number;
  acknowledged_event_ids?: string[];
};

type PostOptions = {
  keepalive?: boolean;
  reportDelivery?: boolean;
};

type PostResult<T> =
  | { ok: true; data: T; status: number }
  | { ok: false; status: number; reason: "auth" | "http" | "network" };

const listeners = new Set<() => void>();
const inMemoryQueues = new Map<string, EventQueueEntry[]>();
const removedIdsByStudent = new Map<string, Set<string>>();
// Only an acknowledgment of this request can report its delivery as successful.
const requestAcknowledgments = new Map<string, Set<() => void>>();
const requestKey = (studentId: string, updateId: string) => `${studentId}:${updateId}`;
const flushesInFlight = new Map<string, Promise<boolean>>();
const failuresByEndpoint = new Map<string, number>();
let readinessProbeInFlight: Promise<void> | null = null;
let snapshot: AnalyticsDeliverySnapshot = {
  status: "unknown",
  consecutiveFailures: 0,
  pendingEventCount: 0,
  lastFailureAt: null,
  lastSuccessAt: null,
  droppedEventCount: 0,
  rejectedEventCount: 0,
  storageFailureCount: 0,
};

type DeliveryHealthCounts = { droppedEventCount: number; rejectedEventCount: number; storageFailureCount: number };
const healthByStudent = new Map<string, DeliveryHealthCounts>();
const healthFor = (studentId: string) => {
  let health = healthByStudent.get(studentId);
  if (!health) {
    health = { droppedEventCount: 0, rejectedEventCount: 0, storageFailureCount: 0 };
    healthByStudent.set(studentId, health);
  }
  return health;
};

const emitSnapshot = (updates: Partial<AnalyticsDeliverySnapshot>) => {
  snapshot = { ...snapshot, ...updates };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getAnalyticsDeliverySnapshot = () => snapshot;

export const useAnalyticsDelivery = () =>
  useSyncExternalStore(subscribe, getAnalyticsDeliverySnapshot, getAnalyticsDeliverySnapshot);

const queueStorageKey = (studentId: string) =>
  `faheem-analytics-events:v${EVENT_QUEUE_VERSION}:${studentId}`;

const getStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const entryKey = (entry: EventQueueEntry) => `${queueStorageKey(entry.studentId)}:${entry.event.client_event_id}`;

// Individual keys make enqueue/acknowledge independent across tabs. Never replace
// a shared array: doing so loses events written while another tab is flushing.
const readQueue = (studentId: string): EventQueueEntry[] => {
  const entries = new Map((inMemoryQueues.get(studentId) ?? []).map(entry => [entry.event.client_event_id, entry]));
  const storage = getStorage();
  if (storage) {
    try {
      const legacyKey = `faheem-analytics-events:v1:${studentId}`;
      const legacy = JSON.parse(storage.getItem(legacyKey) ?? "[]");
      if (Array.isArray(legacy) && legacy.length) {
        for (const entry of legacy) {
          if (entry?.studentId === studentId && entry.event?.client_event_id) {
            storage.setItem(entryKey(entry), JSON.stringify(entry));
          }
        }
        storage.removeItem(legacyKey);
      }
      const prefix = `${queueStorageKey(studentId)}:`;
      for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index);
        if (!key?.startsWith(prefix)) continue;
        try {
          const entry = JSON.parse(storage.getItem(key) ?? "null") as EventQueueEntry | null;
          if (entry?.studentId === studentId && entry.event?.client_event_id) entries.set(entry.event.client_event_id, entry);
        } catch {
          const health = healthFor(studentId);
          health.storageFailureCount++;
          try { storage.removeItem(key); index--; } catch { /* Continue reading the remaining keys. */ }
        }
      }
    } catch { /* The memory copy is authoritative when browser storage fails. */ }
  }
  return [...entries.values()].filter(entry => !removedIdsByStudent.get(studentId)?.has(entry.event.client_event_id)).sort((a, b) => a.event.timestamp.localeCompare(b.event.timestamp) ||
    (a.event.client_sequence ?? 0) - (b.event.client_sequence ?? 0));
};

const persistEntry = (entry: EventQueueEntry) => {
  const queue = inMemoryQueues.get(entry.studentId) ?? [];
  inMemoryQueues.set(entry.studentId, [...queue.filter(item => item.event.client_event_id !== entry.event.client_event_id), entry]);
  try {
    const storage = getStorage();
    if (!storage) throw new Error("Storage unavailable");
    storage.setItem(entryKey(entry), JSON.stringify(entry));
  } catch {
    const health = healthFor(entry.studentId);
    health.storageFailureCount++;
    emitSnapshot(health);
  }
};

const removeEntries = (studentId: string, ids: Set<string>) => {
  const removed = removedIdsByStudent.get(studentId) ?? new Set<string>();
  removedIdsByStudent.set(studentId, removed);
  inMemoryQueues.set(studentId, (inMemoryQueues.get(studentId) ?? []).filter(entry => !ids.has(entry.event.client_event_id)));
  const storage = getStorage();
  for (const id of ids) {
    try {
      storage?.removeItem(`${queueStorageKey(studentId)}:${id}`);
      removed.delete(id);
    } catch { removed.add(id); /* Suppress repeated replay until the next page load. */ }
  }
  emitSnapshot({ pendingEventCount: readQueue(studentId).length });
};

const createEventId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const queueHealthReport = (studentId: string) => {
  const health = healthFor(studentId);
  const old = readQueue(studentId).filter(entry => entry.request?.endpoint === "/telemetry/event-batch" &&
    (entry.request.payload.delivery_health as {client_instance_id?: string})?.client_instance_id === analyticsClientInstanceId);
  const id = createEventId();
  persistEntry({studentId, appSessionId: null, lessonVisitId: null,
    event: {client_event_id: id, event_type: "_request", timestamp: new Date().toISOString()},
    request: {endpoint: "/telemetry/event-batch", payload: {student_id: studentId, events: [], delivery_health: {
      client_instance_id: analyticsClientInstanceId, dropped_events: health.droppedEventCount,
      storage_failures: health.storageFailureCount, rejected_events: health.rejectedEventCount,
    }}},
  });
  removeEntries(studentId, new Set(old.map(entry => entry.event.client_event_id)));
};

const quarantineEntry = (entry: EventQueueEntry, status: number) => {
  try { getStorage()?.setItem(`faheem-analytics-rejected:v1:${entry.studentId}:${entry.event.client_event_id}`,
    JSON.stringify({entry, status, rejected_at: new Date().toISOString()})); } catch { /* Health still records rejection. */ }
  removeEntries(entry.studentId, new Set([entry.event.client_event_id]));
  const health = healthFor(entry.studentId);
  health.rejectedEventCount++;
  emitSnapshot(health);
  queueHealthReport(entry.studentId);
};

const permanentFailure = (status: number) => [400, 404, 409, 413, 422].includes(status);

const probeReadiness = async () => {
  if (readinessProbeInFlight) return readinessProbeInFlight;

  readinessProbeInFlight = (async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/ready`, {
        method: "GET",
        cache: "no-store",
      });
      const stillFailing = [...failuresByEndpoint.values()].some(
        (failures) => failures >= FAILURE_THRESHOLD
      );
      if (stillFailing) {
        emitSnapshot({ status: response.ok ? "degraded" : "offline" });
      }
    } catch {
      const stillFailing = [...failuresByEndpoint.values()].some(
        (failures) => failures >= FAILURE_THRESHOLD
      );
      if (stillFailing) emitSnapshot({ status: "offline" });
    } finally {
      readinessProbeInFlight = null;
    }
  })();

  return readinessProbeInFlight;
};

const reportSuccess = (endpoint: string) => {
  failuresByEndpoint.delete(endpoint);
  const remainingFailures = Math.max(0, ...failuresByEndpoint.values());
  emitSnapshot({
    status:
      remainingFailures >= FAILURE_THRESHOLD
        ? snapshot.status === "offline"
          ? "offline"
          : "degraded"
        : "healthy",
    consecutiveFailures: remainingFailures,
    lastSuccessAt: Date.now(),
  });
};

const reportFailure = (
  endpoint: string,
  reason: "auth" | "http" | "network"
) => {
  const failures = (failuresByEndpoint.get(endpoint) ?? 0) + 1;
  failuresByEndpoint.set(endpoint, failures);

  if (reason === "auth") {
    emitSnapshot({
      status: "unauthenticated",
      consecutiveFailures: failures,
      lastFailureAt: Date.now(),
    });
    return;
  }

  emitSnapshot({
    consecutiveFailures: failures,
    lastFailureAt: Date.now(),
  });
  if (failures >= FAILURE_THRESHOLD) void probeReadiness();
};

const getAccessToken = async () => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
};

export async function postAnalyticsJson<T>(
  endpoint: string,
  data: Record<string, unknown>,
  options: PostOptions = {}
): Promise<PostResult<T>> {
  const { keepalive = false, reportDelivery = true } = options;
  const accessToken = await getAccessToken();
  if (!accessToken) {
    if (reportDelivery) reportFailure(endpoint, "auth");
    return { ok: false, status: 401, reason: "auth" };
  }

  try {
    const response = await fetch(`${BACKEND_API_URL}${endpoint}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
      keepalive,
    });

    if (!response.ok) {
      if (reportDelivery) {
        reportFailure(endpoint, response.status === 401 ? "auth" : "http");
      }
      return {
        ok: false,
        status: response.status,
        reason: response.status === 401 ? "auth" : "http",
      };
    }

    const responseData = (await response.json()) as T;
    if (reportDelivery) reportSuccess(endpoint);
    return { ok: true, data: responseData, status: response.status };
  } catch {
    if (reportDelivery) reportFailure(endpoint, "network");
    return { ok: false, status: 0, reason: "network" };
  }
}

const enforceQueueBound = (studentId: string) => {
  const queue = readQueue(studentId);
  if (queue.length > MAX_QUEUED_EVENTS) {
    const overflow = queue.slice(0, queue.length - MAX_QUEUED_EVENTS + 1);
    removeEntries(studentId, new Set(overflow.map(entry => entry.event.client_event_id)));
    const health = healthFor(studentId);
    health.droppedEventCount += overflow.length;
    emitSnapshot(health);
    queueHealthReport(studentId);
  }
};

export const enqueueAnalyticsEvents = (
  studentId: string,
  appSessionId: number | null,
  lessonVisitId: number | null,
  events: Array<Omit<QueuedAnalyticsEvent, "client_event_id"> & { client_event_id?: string }>
) => {
  for (const event of events) persistEntry({
    studentId, appSessionId, lessonVisitId,
    event: { ...event, client_event_id: event.client_event_id ?? createEventId(),
      schema_version: 2, client_instance_id: analyticsClientInstanceId,
      client_sequence: ++clientSequence, app_build: import.meta.env.VITE_APP_BUILD ?? "unversioned" },
  });
  enforceQueueBound(studentId);
  if (healthFor(studentId).storageFailureCount > 0) queueHealthReport(studentId);
  emitSnapshot({ pendingEventCount: readQueue(studentId).length });
};

export async function queueAnalyticsRequest(endpoint: string, payload: Record<string, unknown>, keepalive = false) {
  const studentId = String(payload.student_id);
  const updateId = typeof payload.update_id === "string" ? payload.update_id : createEventId();
  const superseded = endpoint.endsWith("/heartbeat") ? readQueue(studentId).filter(entry =>
    entry.request?.endpoint === endpoint && entry.request.payload.client_instance_id === analyticsClientInstanceId &&
    entry.request.payload.app_session_id === payload.app_session_id && entry.request.payload.lesson_visit_id === payload.lesson_visit_id
  ) : [];
  persistEntry({ studentId, appSessionId: typeof payload.app_session_id === "number" ? payload.app_session_id : null,
    lessonVisitId: typeof payload.lesson_visit_id === "number" ? payload.lesson_visit_id : null,
    event: { client_event_id: updateId, event_type: "_request", timestamp: new Date().toISOString(), client_sequence: ++clientSequence },
    request: { endpoint, payload: { ...payload, update_id: updateId, client_instance_id: analyticsClientInstanceId } },
  });
  removeEntries(studentId, new Set(superseded.map(entry => entry.event.client_event_id)));
  enforceQueueBound(studentId);
  const key = requestKey(studentId, updateId);
  const listeners = requestAcknowledgments.get(key) ?? new Set<() => void>();
  let acknowledged = false;
  const onAcknowledged = () => { acknowledged = true; };
  listeners.add(onAcknowledged);
  requestAcknowledgments.set(key, listeners);
  try {
    await flushQueuedAnalyticsEvents(studentId, typeof payload.app_session_id === "number" ? payload.app_session_id : null, { keepalive });
    return acknowledged;
  } finally {
    listeners.delete(onAcknowledged);
    if (listeners.size === 0) requestAcknowledgments.delete(key);
  }
}

export const flushQueuedAnalyticsEvents = async (
  studentId: string,
  _currentAppSessionId: number | null,
  options: { keepalive?: boolean } = {}
): Promise<boolean> => {
  const existingFlush = flushesInFlight.get(studentId);
  if (existingFlush) {
    const completed = await existingFlush;
    return completed ? flushQueuedAnalyticsEvents(studentId, _currentAppSessionId, options) : false;
  }

  const flushPromise = (async () => {
    let queue = readQueue(studentId);
    const health = healthFor(studentId);
    emitSnapshot({ pendingEventCount: queue.length });
    if (queue.length === 0) return true;

    let batchSize = EVENT_BATCH_SIZE;
    while (queue.length > 0) {
      const first = queue[0];
      if (first.request) {
        const result = await postAnalyticsJson(first.request.endpoint, first.request.payload, { keepalive: options.keepalive });
        if (!result.ok) {
          if (permanentFailure(result.status) && first.request.endpoint !== "/telemetry/event-batch") {
            quarantineEntry(first, result.status);
            queue = readQueue(studentId);
            continue;
          }
          return false;
        }
        requestAcknowledgments.get(requestKey(studentId, first.event.client_event_id))?.forEach(notify => notify());
        removeEntries(studentId, new Set([first.event.client_event_id]));
        queue = readQueue(studentId);
        continue;
      }
      // Preserve the original unknown context instead of attaching old events to a new session.
      const resolvedAppSessionId = first.appSessionId;
      const batch = queue
        .filter(
          (entry) =>
            !entry.request && entry.appSessionId === resolvedAppSessionId &&
            entry.lessonVisitId === first.lessonVisitId
        )
        .slice(0, batchSize);

      const result = await postAnalyticsJson<EventBatchResponse>(
        "/telemetry/event-batch",
        {
          student_id: studentId,
          app_session_id: resolvedAppSessionId,
          lesson_visit_id: first.lessonVisitId,
          events: batch.map((entry) => entry.event),
          delivery_health: { client_instance_id: analyticsClientInstanceId,
            dropped_events: health.droppedEventCount, storage_failures: health.storageFailureCount,
            rejected_events: health.rejectedEventCount },
        },
        { keepalive: options.keepalive }
      );

      if (!result.ok) {
        if (permanentFailure(result.status)) {
          if (batch.length > 1) { batchSize = 1; continue; }
          quarantineEntry(first, result.status);
          queue = readQueue(studentId);
          continue;
        }
        return false;
      }
      batchSize = EVENT_BATCH_SIZE;

      const submittedIds = batch.map((entry) => entry.event.client_event_id);
      const acknowledgedIds = Array.isArray(result.data.acknowledged_event_ids)
        ? result.data.acknowledged_event_ids
        : [];

      const acknowledged = new Set(acknowledgedIds);
      if (acknowledged.size !== submittedIds.length || submittedIds.some(id => !acknowledged.has(id))) {
        reportFailure("/telemetry/event-batch", "http");
        return false;
      }

      removeEntries(studentId, acknowledged);
      queue = readQueue(studentId);
    }

    return true;
  })();

  flushesInFlight.set(studentId, flushPromise);

  try {
    return await flushPromise;
  } finally {
    if (flushesInFlight.get(studentId) === flushPromise) {
      flushesInFlight.delete(studentId);
    }
  }
};

export const syncAnalyticsQueueCount = (studentId: string) => {
  emitSnapshot({ pendingEventCount: readQueue(studentId).length });
};

export const retryAnalyticsDelivery = async (
  studentId: string,
  appSessionId: number
): Promise<boolean> => {
  const queueFlushed = await flushQueuedAnalyticsEvents(studentId, appSessionId);
  if (!queueFlushed) return false;

  const statusResult = await postAnalyticsJson<{ app_session_valid: boolean }>(
    "/telemetry/app-session/status",
    {
      student_id: studentId,
      app_session_id: appSessionId,
    },
    { reportDelivery: false }
  );
  if (!statusResult.ok) return false;

  failuresByEndpoint.clear();
  emitSnapshot({
    status: "healthy",
    consecutiveFailures: 0,
    lastSuccessAt: Date.now(),
  });
  return true;
};
